import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, BookOpen, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Navbar from "../components/storyforge/Navbar";
import { api, getUser, imageUrl } from "../lib/api";
import { SF } from "../constants/testIds";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const STATUS_LABEL = {
    pending_payment: "Awaiting payment",
    pending: "Queued",
    generating_story: "Writing",
    story_generated: "Story ready",
    images_generating: "Illustrating",
    assembling_pdf: "Binding",
    complete: "Print-ready",
    sent_to_print: "At the printer",
    shipped: "Shipped",
    failed: "Failed",
};

export default function Dashboard() {
    useDocumentMeta({
        title: "Your library — Athena's Atlas",
        description: "Track your Athena's Atlas storybook orders, view past books, download print-ready PDFs and start sequels for the same child.",
    });
    const navigate = useNavigate();
    const user = getUser();
    const userId = user?.id;
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) {
            navigate("/login");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/orders");
                if (!cancelled) setOrders(data);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [userId, navigate]);

    return (
        <div className="min-h-screen paper-bg">
            <Navbar />
            <div className="max-w-6xl mx-auto px-6 md:px-10 pt-12 pb-24">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
                    <div>
                        <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-3">
                            Hello{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}
                        </p>
                        <h1 className="font-serif text-4xl md:text-5xl text-primary tracking-tight">Your storybooks</h1>
                    </div>
                    <Link to="/create" data-testid={SF.dashCreateNew}>
                        <Button className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6">
                            <Plus className="w-4 h-4 mr-2" /> New book
                        </Button>
                    </Link>
                </div>

                {loading ? (
                    <div className="paper-card rounded-3xl p-12 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                    </div>
                ) : orders.length === 0 ? (
                    <div data-testid={SF.dashEmpty} className="paper-card rounded-3xl p-12 md:p-20 text-center">
                        <BookOpen className="w-10 h-10 mx-auto text-accent mb-6" strokeWidth={1.5} />
                        <h2 className="font-serif text-3xl text-primary mb-3">No books yet.</h2>
                        <p className="text-muted-foreground max-w-md mx-auto mb-8">Every great bedtime begins with the first page. Tell us about your child and we&apos;ll do the rest.</p>
                        <Link to="/create">
                            <Button className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6">Create your first book</Button>
                        </Link>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                        {orders.map((o, i) => (
                            <motion.div
                                key={o.id}
                                data-testid={SF.dashOrderCard}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                            >
                                <Link to={`/order/${o.id}`} className="block paper-card rounded-3xl p-7 hover:-translate-y-1 transition-transform">
                                    <div className="flex items-start gap-5 mb-4">
                                        {o.cover_front_image_url ? (
                                            <img
                                                data-testid="sf-dash-cover-thumb"
                                                src={imageUrl(o.cover_front_image_url)}
                                                alt={`${o.child_name}'s book cover`}
                                                className="w-20 h-20 md:w-24 md:h-24 rounded-xl object-cover shadow-md shrink-0 bg-muted"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl bg-accent/30 flex items-center justify-center shrink-0">
                                                <BookOpen className="w-8 h-8 text-accent-foreground/50" />
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-3 mb-1">
                                                <h3 className="font-serif text-xl md:text-2xl text-primary leading-tight">
                                                    {o.book_title || `${o.child_name} and the ${o.theme}`}
                                                </h3>
                                            </div>
                                            <Badge variant="outline" className="border-primary/30 bg-muted/40">
                                                {STATUS_LABEL[o.status] || o.status}
                                            </Badge>
                                        </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground italic mb-4 line-clamp-2">{o.lesson}</p>
                                    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
                                        <span>{o.style}</span>
                                        <span>{new Date(o.created_at).toLocaleDateString()}</span>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
